import React, { useState, useRef, useEffect } from 'react';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    IconButton,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Checkbox,
    Tooltip,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import './DynamicGrid.css';

/**
 * DynamicGrid - A flexible grid component with inline editing support
 * 
 * Features:
 * - Inline cell editing with different input types
 * - Column management (add, edit, delete)
 * - Row selection and bulk operations
 * - Support for multiple data types
 * 
 * Data Types Supported:
 * - 'string': Text input (default)
 * - 'numeric': Number input
 * - 'boolean': Yes/No select dropdown
 * - 'date': Date picker
 * - 'select': Custom select dropdown with options
 * 
 * Column Structure:
 * {
 *   name: 'field_name',           // Internal field name
 *   displayName: 'Display Name',  // Name shown in header
 *   dataType: 'string',           // One of the supported data types
 *   options?: [                   // Required for 'select' type
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' }
 *   ],
 *   columnHeaderEditable?: boolean // If false, column title/type are not editable (default: true)
 * }
 * 
 * Props:
 * - data: Array of row objects
 * - columns: Array of column definitions
 * - onDataChange: Callback when data changes
 * - onColumnAdd: Callback when column is added
 * - onColumnEdit: Callback when column is edited
 * - onCellUpdate: Callback when cell is updated (for backend sync)
 * - onRowDelete: Callback when rows are deleted
 * - onRowAdd: Callback when new row is added
 * - editable: Whether grid is editable (default: true)
 * - maxHeight: Maximum height of grid (default: '600px')
 * - maxWidth: Maximum width of grid (default: '100%')
 * - rowIdField: Field name containing database row ID (default: 'id')
 */
const DynamicGrid = ({ 
    data = [], 
    columns = [], 
    onDataChange,
    onColumnAdd,
    onColumnEdit,
    onCellUpdate,
    onRowDelete,
    onRowAdd,
    editable = true,
    maxHeight = '600px',
    maxWidth = '100%',
    rowIdField = 'id' // Field name that contains the database row ID
}) => {
    // Ensure we have at least one row for editing
    const displayData = data.length > 0 ? data : [{}];

    const [showAddColumnDialog, setShowAddColumnDialog] = useState(false);
    const [showEditColumnDialog, setShowEditColumnDialog] = useState(false);
    const [editingColumn, setEditingColumn] = useState(null);
    const [newColumnData, setNewColumnData] = useState({
        name: '',
        displayName: '',
        dataType: 'string',
        options: [] // For select fields
    });
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [hoveredRow, setHoveredRow] = useState(null);
    const tableRef = useRef(null);

    const dataTypes = [
        { value: 'string', label: 'Text' },
        { value: 'numeric', label: 'Number' },
        { value: 'boolean', label: 'Boolean' },
        { value: 'date', label: 'Date' },
        { value: 'select', label: 'Select' }
    ];

    // Helper function to get display value for a cell
    const getDisplayValue = (value, column) => {
        if (value === null || value === undefined || value === '') {
            return 'Click to edit';
        }
        
        if (column.dataType === 'select' && column.options) {
            const option = column.options.find(opt => opt.value === value);
            return option ? option.label : value;
        }
        return value;
    };

    // Helper function to render the appropriate input component
    const renderInputComponent = (column, value, onChange, onKeyDown, onBlur) => {
        switch (column.dataType) {
            case 'select':
                // Ensure all option values are strings
                const stringOptions = column.options?.map(opt => ({
                    value: String(opt.value),
                    label: opt.label
                })) || [];
                return (
                    <FormControl fullWidth size="small" sx={{ minHeight: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <Select
                            value={value === null || value === undefined ? '' : String(value)}
                            onChange={(e) => {
                                const selectedValue = e.target.value;
                                const finalValue = selectedValue === '' ? null : selectedValue;
                                setEditValue(finalValue);
                                handleCellSave(finalValue);
                            }}
                            onKeyDown={onKeyDown}
                            autoFocus
                            size="small"
                            sx={{
                                fontSize: '14px',
                                minHeight: '24px',
                                height: '24px',
                                lineHeight: '24px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                '& .MuiSelect-select': {
                                    minHeight: '24px !important',
                                    height: '24px !important',
                                    lineHeight: '24px !important',
                                    padding: '0px 8px !important',
                                    display: 'flex',
                                    alignItems: 'center',
                                    alignSelf: 'center',
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '0px 8px !important',
                                    minHeight: '24px !important',
                                    height: '24px !important',
                                    lineHeight: '24px !important',
                                    display: 'flex',
                                    alignItems: 'center',
                                    alignSelf: 'center',
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 200,
                                    },
                                },
                            }}
                        >
                            <MenuItem value="">
                                <em>Select...</em>
                            </MenuItem>
                            {stringOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            
            case 'boolean':
                return (
                    <Select
                        value={value === null || value === undefined ? '' : String(value)}
                        onChange={(e) => {
                            const selectedValue = e.target.value;
                            const finalValue = selectedValue === '' ? null : selectedValue;
                            setEditValue(finalValue);
                            handleCellSave(finalValue);
                        }}
                        onKeyDown={onKeyDown}
                        autoFocus
                        size="small"
                        sx={{
                            fontSize: '14px',
                            minHeight: '24px',
                            height: '24px',
                            lineHeight: '24px',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            '& .MuiSelect-select': {
                                minHeight: '24px !important',
                                height: '24px !important',
                                lineHeight: '24px !important',
                                padding: '0px 8px !important',
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'center',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: '0px 8px !important',
                                minHeight: '24px !important',
                                height: '24px !important',
                                lineHeight: '24px !important',
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'center',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                            }
                        }}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 200,
                                },
                            },
                        }}
                    >
                        <MenuItem value="">Select...</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                    </Select>
                );
            
            case 'date':
                return (
                    <input
                        type="date"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={onKeyDown}
                        onBlur={onBlur}
                        autoFocus
                        style={{
                            width: '100%',
                            height: '24px',
                            lineHeight: '24px',
                            fontSize: '14px',
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            margin: 0,
                            padding: 0,
                            fontFamily: 'inherit',
                            color: 'inherit',
                            boxSizing: 'border-box',
                            verticalAlign: 'middle',
                            display: 'block'
                        }}
                    />
                );
            
            case 'numeric':
                return (
                    <input
                        type="number"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={onKeyDown}
                        onBlur={onBlur}
                        autoFocus
                        style={{
                            width: '100%',
                            height: '24px',
                            lineHeight: '24px',
                            fontSize: '14px',
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            margin: 0,
                            padding: 0,
                            fontFamily: 'inherit',
                            color: 'inherit',
                            boxSizing: 'border-box',
                            verticalAlign: 'middle',
                            display: 'block'
                        }}
                    />
                );
            
            default: // string
                return (
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={onKeyDown}
                        onBlur={onBlur}
                        autoFocus
                        style={{
                            width: '100%',
                            height: '24px',
                            lineHeight: '24px',
                            fontSize: '14px',
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            margin: 0,
                            padding: 0,
                            fontFamily: 'inherit',
                            color: 'inherit',
                            boxSizing: 'border-box',
                            verticalAlign: 'middle',
                            display: 'block'
                        }}
                    />
                );
        }
    };

    // Get database row ID for a given display index
    const getRowId = (displayIndex) => {
        const row = data[displayIndex];
        if (!row) return null;
        
        // If rowIdField exists, use it; otherwise fall back to display index
        const rowId = row[rowIdField];
        return rowId !== undefined && rowId !== null ? rowId : displayIndex;
    };

    // Get display index for a given database row ID
    const getDisplayIndex = (rowId) => {
        return data.findIndex(row => {
            const currentRowId = row[rowIdField];
            return currentRowId !== undefined && currentRowId !== null ? currentRowId === rowId : false;
        });
    };

    // Handle select all checkbox
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allRowIds = data
                .map((row, index) => getRowId(index))
                .filter(id => id !== null);
            setSelectedRows(new Set(allRowIds));
        } else {
            setSelectedRows(new Set());
        }
    };

    // Handle individual row selection
    const handleRowSelect = (displayIndex) => {
        const rowId = getRowId(displayIndex);
        if (rowId === null) return;

        const newSelectedRows = new Set(selectedRows);
        if (newSelectedRows.has(rowId)) {
            newSelectedRows.delete(rowId);
        } else {
            newSelectedRows.add(rowId);
        }
        setSelectedRows(newSelectedRows);
    };

    // Handle row deletion
    const handleDeleteSelectedRows = () => {
        if (selectedRows.size > 0 && onRowDelete) {
            const rowIds = Array.from(selectedRows).sort((a, b) => a - b);
            onRowDelete(rowIds);
            setSelectedRows(new Set());
        }
    };

    // Check if all rows are selected
    const allRowIds = data
        .map((row, index) => getRowId(index))
        .filter(id => id !== null);
    const isAllSelected = allRowIds.length > 0 && selectedRows.size === allRowIds.length;
    const isIndeterminate = selectedRows.size > 0 && selectedRows.size < allRowIds.length;

    // Helper function to check if a row is selected
    const isRowSelected = (displayIndex) => {
        const rowId = getRowId(displayIndex);
        return rowId !== null && selectedRows.has(rowId);
    };

    const handleAddColumn = () => {
        if (newColumnData.displayName) {
            // Derive column name from display name
            const derivedName = newColumnData.displayName
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '_')
                .replace(/_+/g, '_')
                .replace(/^_|_$/g, '');
            
            const columnData = {
                name: derivedName,
                displayName: newColumnData.displayName,
                dataType: newColumnData.dataType,
                options: newColumnData.dataType === 'select' ? newColumnData.options : undefined
            };
            
            onColumnAdd?.(columnData);
            setNewColumnData({ name: '', displayName: '', dataType: 'string', options: [] });
            setShowAddColumnDialog(false);
        }
    };

    const handleEditColumn = () => {
        if (editingColumn && newColumnData.displayName) {
            onColumnEdit?.(editingColumn.name, newColumnData);
            setShowEditColumnDialog(false);
            setEditingColumn(null);
            setNewColumnData({ name: '', displayName: '', dataType: 'string', options: [] });
        }
    };

    const handleCellEdit = (rowIndex, columnName) => {
        const currentValue = data[rowIndex]?.[columnName];
        const rowId = getRowId(rowIndex);
        setEditingCell({ rowId, rowIndex, columnName });
        setEditValue(currentValue);
    };

    const handleCellSave = (overrideValue) => {
        let value = overrideValue !== undefined ? overrideValue : editValue;

        // If value is an event object, ignore or extract value
        if (value && value.nativeEvent && value.target && value.target.value !== undefined) {
            value = value.target.value;
        }
        // If value is an object (not null), stringify it or set to empty string
        if (typeof value === 'object' && value !== null) {
            value = JSON.stringify(value);
        }

        if (editingCell && onDataChange) {
            const newData = [...data];
            if (!newData[editingCell.rowIndex]) {
                newData[editingCell.rowIndex] = {};
            }
            newData[editingCell.rowIndex][editingCell.columnName] = value;
            onDataChange(newData);

            if (onCellUpdate) {
                const column = columns.find(col => col.name === editingCell.columnName);
                let valueToSend = value;

                // Handle select fields - convert to number if not null/empty
                if (column?.dataType === 'select' && valueToSend !== null && valueToSend !== '') {
                    valueToSend = Number(valueToSend);
                }

                // Convert empty strings to null for select and boolean fields
                if ((column?.dataType === 'select' || column?.dataType === 'boolean') && valueToSend === '') {
                    valueToSend = null;
                }

                console.log('handleCellSave:', { valueToSend, editValue: value, column });
                onCellUpdate(editingCell.rowId, editingCell.columnName, valueToSend);
            }
        }
        setEditingCell(null);
        setEditValue('');
    };

    const handleCellCancel = () => {
        setEditingCell(null);
        setEditValue('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleCellSave();
        } else if (e.key === 'Escape') {
            handleCellCancel();
        }
    };

    const openEditColumnDialog = (column) => {
        setEditingColumn(column);
        setNewColumnData({
            name: column.name,
            displayName: column.displayName,
            dataType: column.dataType || 'string',
            options: column.options || []
        });
        setShowEditColumnDialog(true);
    };

    return (
        <Box sx={{ width: maxWidth, maxWidth: '100%' }}>
            {/* Row Actions Bar */}
            {selectedRows.size > 0 && (
                <Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        p: 1, 
                        mb: 1, 
                        backgroundColor: '#e3f2fd', 
                        borderRadius: 1,
                        border: '1px solid #bbdefb'
                    }}
                >
                    <Typography variant="body2" color="primary">
                        {selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''} selected
                    </Typography>
                    <Tooltip title="Delete selected rows">
                        <IconButton 
                            onClick={handleDeleteSelectedRows}
                            color="error"
                            size="small"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            <TableContainer 
                component={Paper} 
                sx={{ 
                    maxHeight, 
                    maxWidth: '100%',
                    overflow: 'auto'
                }}
                ref={tableRef}
            >
                <Table stickyHeader size="small" className="dynamic-grid-table">
                    <TableHead>
                        <TableRow>
                            {/* Combined checkbox and row number header */}
                            <TableCell 
                                sx={{ 
                                    minWidth: 80, 
                                    backgroundColor: '#f5f5f5',
                                    fontWeight: 'bold',
                                    borderRight: '1px solid #e0e0e0',
                                    borderBottom: '2px solid #e0e0e0',
                                    textAlign: 'center'
                                }}
                            >
                                <Checkbox
                                    checked={isAllSelected}
                                    indeterminate={isIndeterminate}
                                    onChange={handleSelectAll}
                                    size="small"
                                />
                            </TableCell>
                            
                            {/* Column headers */}
                            {columns.map((column, index) => (
                                <TableCell 
                                    key={column.name}
                                    sx={{ 
                                        minWidth: 150,
                                        backgroundColor: '#f5f5f5',
                                        fontWeight: 'bold',
                                        position: 'relative',
                                        borderRight: '1px solid #e0e0e0',
                                        borderBottom: '2px solid #e0e0e0'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" noWrap>
                                            {column.displayName}
                                        </Typography>
                                        {editable && (column.columnHeaderEditable !== false) && (
                                            <IconButton
                                                size="small"
                                                onClick={() => openEditColumnDialog(column)}
                                                sx={{ ml: 1 }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                            
                            {/* Add column header */}
                            {editable && (
                                <TableCell 
                                    sx={{ 
                                        minWidth: 120,
                                        backgroundColor: '#f5f5f5',
                                        borderLeft: '1px solid #e0e0e0'
                                    }}
                                >
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={() => setShowAddColumnDialog(true)}
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                    >
                                        Add Column
                                    </Button>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    
                    <TableBody>
                        {displayData.map((row, rowIndex) => (
                            <TableRow 
                                key={rowIndex} 
                                hover
                                onMouseEnter={() => setHoveredRow(rowIndex)}
                                onMouseLeave={() => setHoveredRow(null)}
                                sx={{
                                    backgroundColor: isRowSelected(rowIndex) ? '#e8f5e8' : 'inherit',
                                    '&:hover': {
                                        backgroundColor: isRowSelected(rowIndex) ? '#d4edda' : '#f5f5f5'
                                    }
                                }}
                            >
                                {/* Combined checkbox and row number */}
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: isRowSelected(rowIndex) ? '#e8f5e8' : '#fafafa',
                                        borderRight: '1px solid #e0e0e0',
                                        borderBottom: '1px solid #e0e0e0',
                                        textAlign: 'center',
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {/* Checkbox - positioned absolutely to overlay row number */}
                                    <Box 
                                        sx={{ 
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            display: hoveredRow === rowIndex || isRowSelected(rowIndex) ? 'block' : 'none',
                                            transition: 'opacity 0.2s ease-in-out',
                                            zIndex: 1
                                        }}
                                    >
                                        <Checkbox
                                            checked={isRowSelected(rowIndex)}
                                            onChange={() => handleRowSelect(rowIndex)}
                                            size="small"
                                        />
                                    </Box>
                                    
                                    {/* Row number - always present but hidden when checkbox is shown */}
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontWeight: 'bold', 
                                            minWidth: '20px',
                                            opacity: (hoveredRow === rowIndex || isRowSelected(rowIndex)) ? 0 : 1,
                                            transition: 'opacity 0.2s ease-in-out',
                                            visibility: (hoveredRow === rowIndex || isRowSelected(rowIndex)) ? 'hidden' : 'visible'
                                        }}
                                    >
                                        {rowIndex + 1}
                                    </Typography>
                                </TableCell>
                                
                                {/* Data cells */}
                                {columns.map((column) => {
                                    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnName === column.name;
                                    const cellValue = row[column.name];
                                    
                                    return (
                                        <TableCell 
                                            key={column.name}
                                            className={isEditing ? 'dynamic-grid-cell-editing' : ''}
                                            sx={{ 
                                                minWidth: 150,
                                                cursor: editable ? 'pointer' : 'default',
                                                position: 'relative',
                                                height: '40px',
                                                padding: '8px',
                                                borderRight: '1px solid #e0e0e0',
                                                borderBottom: '1px solid #e0e0e0',
                                                backgroundColor: isRowSelected(rowIndex) ? '#e8f5e8' : 'inherit'
                                            }}
                                            onClick={() => editable && handleCellEdit(rowIndex, column.name)}
                                        >
                                            {isEditing ? (
                                                renderInputComponent(column, editValue, (val) => {
                                                    console.log('setEditValue called with:', val);
                                                    setEditValue(val);
                                                }, handleKeyPress, handleCellSave)
                                            ) : (
                                                <Typography 
                                                    variant="body2" 
                                                    noWrap
                                                    sx={{ 
                                                        color: cellValue ? 'inherit' : '#999',
                                                        fontStyle: cellValue ? 'normal' : 'italic'
                                                    }}
                                                >
                                                    {getDisplayValue(cellValue, column)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    );
                                })}
                                
                                {/* Empty cell for add column button alignment */}
                                {editable && (
                                    <TableCell 
                                        sx={{ 
                                            borderLeft: '1px solid #e0e0e0',
                                            backgroundColor: isRowSelected(rowIndex) ? '#e8f5e8' : '#fafafa'
                                        }}
                                    />
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add Row Button */}
            {editable && onRowAdd && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={onRowAdd}
                        size="small"
                    >
                        +New row
                    </Button>
                </Box>
            )}

            {/* Add Column Dialog */}
            <Dialog open={showAddColumnDialog} onClose={() => setShowAddColumnDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Column</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Column Name"
                        fullWidth
                        variant="outlined"
                        value={newColumnData.displayName}
                        onChange={(e) => setNewColumnData({ ...newColumnData, displayName: e.target.value })}
                        helperText="Name shown in the table header"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        select
                        margin="dense"
                        label="Data Type"
                        fullWidth
                        variant="outlined"
                        value={newColumnData.dataType}
                        onChange={(e) => setNewColumnData({ ...newColumnData, dataType: e.target.value })}
                        helperText="Type of data this column will store"
                    >
                        {dataTypes.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </TextField>
                    {newColumnData.dataType === 'select' && (
                        <TextField
                            margin="dense"
                            label="Options (comma-separated)"
                            fullWidth
                            variant="outlined"
                            value={newColumnData.options.map(opt => opt.label).join(', ')}
                            onChange={(e) => setNewColumnData({
                                ...newColumnData,
                                options: e.target.value.split(',').map(opt => ({ value: opt.trim(), label: opt.trim() })).filter(opt => opt.value)
                            })}
                            helperText="Options for the select field (e.g., 'Option1, Option2, Option3')"
                            sx={{ mt: 2 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowAddColumnDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddColumn} variant="contained">Add Column</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Column Dialog */}
            <Dialog open={showEditColumnDialog} onClose={() => setShowEditColumnDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Column</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Column Name (internal)"
                        fullWidth
                        variant="outlined"
                        value={newColumnData.name}
                        disabled
                        helperText="Internal name cannot be changed"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Display Name"
                        fullWidth
                        variant="outlined"
                        value={newColumnData.displayName}
                        onChange={(e) => setNewColumnData({ ...newColumnData, displayName: e.target.value })}
                        helperText="Name shown in the table header"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        select
                        margin="dense"
                        label="Data Type"
                        fullWidth
                        variant="outlined"
                        value={newColumnData.dataType}
                        onChange={(e) => setNewColumnData({ ...newColumnData, dataType: e.target.value })}
                        helperText="Type of data this column will store"
                    >
                        {dataTypes.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </TextField>
                    {newColumnData.dataType === 'select' && (
                        <TextField
                            margin="dense"
                            label="Options (comma-separated)"
                            fullWidth
                            variant="outlined"
                            value={newColumnData.options.map(opt => opt.label).join(', ')}
                            onChange={(e) => setNewColumnData({
                                ...newColumnData,
                                options: e.target.value.split(',').map(opt => ({ value: opt.trim(), label: opt.trim() })).filter(opt => opt.value)
                            })}
                            helperText="Options for the select field (e.g., 'Option1, Option2, Option3')"
                            sx={{ mt: 2 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowEditColumnDialog(false)}>Cancel</Button>
                    <Button onClick={handleEditColumn} variant="contained">Save Changes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DynamicGrid; 